import React from 'react'
import {
  Aggregate,
  AggregateColumnDirective,
  AggregateColumnsDirective,
  AggregateDirective,
  AggregatesDirective,
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Resize,
  Sort,
} from '@syncfusion/ej2-react-grids'
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'

import './DashboardStyle.css'

type DataGridColumn = {
  field: string
  header: string
  template?: (item: any) => React.ReactElement
  isDropdown?: boolean
  width?: string
  headerTemplate?: (item: any) => React.ReactElement
}

type DataGridProps = {
  title: string
  data: any[]
  columns: DataGridColumn[]
  allowSorting: boolean
  allowMultiSorting: boolean
  filterSelect?: (field: string, value: string) => void
  currFilterValue?: { field: string; value: string }
  dropdownOptions?: string[]
  classNameTable?: string
  isRowClickable?: boolean
  onClickRow?: () => void
  shouldShowFooter?: boolean
  footerData?: any[]
  detailTemplate?: any
  allowPaging?: boolean
}

type CustomDropdownProps = {
  field: string
  options: string[] | undefined
  onSelect: ((field: string, value: string) => void) | undefined
  selectedValue: string
  placeholder: string
}

const DashboardTable = ({
  title,
  data,
  columns,
  allowSorting,
  allowMultiSorting,
  filterSelect,
  currFilterValue,
  dropdownOptions,
  classNameTable,
  isRowClickable,
  onClickRow,
  shouldShowFooter,
  footerData,
  detailTemplate,
  allowPaging,
}: DataGridProps): React.ReactElement => {
  const CustomDropdown = ({
    field,
    options,
    onSelect,
    selectedValue,
    placeholder,
  }: CustomDropdownProps): React.ReactElement => {
    const capitalizeFirstLetter = (text: string): string => {
      return text.charAt(0).toUpperCase() + text.slice(1)
    }

    return (
      <DropDownListComponent
        id={`${field}-dropdown`}
        dataSource={options}
        value={selectedValue}
        change={(e) => onSelect && onSelect(field, e.value)}
        placeholder={capitalizeFirstLetter(placeholder)}
        popupHeight="200px"
        width="100%"
      />
    )
  }

  return (
    <div className="control-pane mt-1" id={title}>
      <div
        className={`control-section dashboard-table ${classNameTable || ''}`}
      >
        <GridComponent
          key={title}
          dataSource={data}
          allowTextWrap={true}
          pageSettings={{ pageCount: 5 }}
          allowSorting={allowSorting}
          allowMultiSorting={allowMultiSorting}
          rowSelected={isRowClickable ? onClickRow : undefined}
          detailTemplate={detailTemplate}
          allowPaging={allowPaging}
        >
          <ColumnsDirective>
            {columns.map(
              (
                { header, field, isDropdown, template, width, headerTemplate },
                index,
              ) => (
                <ColumnDirective
                  key={index}
                  field={field as string}
                  headerText={header}
                  headerTemplate={
                    (isDropdown &&
                    (field === 'location' || field === 'provider')
                      ? () => (
                          <CustomDropdown
                            field={field}
                            options={dropdownOptions}
                            onSelect={filterSelect}
                            selectedValue={
                              currFilterValue?.field === field
                                ? currFilterValue?.value
                                : ''
                            }
                            placeholder={`${dropdownOptions && dropdownOptions[0]}`}
                          />
                        )
                      : undefined) || headerTemplate
                  }
                  width={width ?? '100'}
                  textAlign="Left"
                  template={template}
                  allowSorting={true}
                />
              ),
            )}
          </ColumnsDirective>
          {shouldShowFooter && (
            <AggregatesDirective>
              <AggregateDirective>
                <AggregateColumnsDirective>
                  {footerData?.map(({ field, template }) => (
                    <AggregateColumnDirective
                      key={`footer-${field}`}
                      field={field}
                      type={'Sum'}
                      footerTemplate={template}
                    />
                  ))}
                </AggregateColumnsDirective>
              </AggregateDirective>
            </AggregatesDirective>
          )}
          <Inject services={[Page, Sort, Resize, Aggregate]} />
        </GridComponent>
      </div>
    </div>
  )
}

export default DashboardTable
